import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { toast } from 'sonner';

import {
  threadHistoryState,
  useChatData,
  useChatMessages
} from '@chainlit/react-client';

import {
  type RunInfo,
  getRuns,
  isActive,
  markRunSeen,
  refreshRuns,
  useRuns
} from './api';

/**
 * Keeps the conversation states fresh and says when something happened
 * elsewhere.
 *
 * Polls quickly while anything is in flight (or this tab is answering) and
 * slowly otherwise, so a second device's answer still shows up. A finished
 * answer in another conversation, or one that stopped to ask the user
 * something, gets a small toast with a way to open it. Opening a ready or
 * failed conversation clears its dot.
 */

const FAST_MS = 2000;
const SLOW_MS = 15000;

export default function RunsWatcher() {
  const navigate = useNavigate();
  const { loading } = useChatData();
  const { threadId } = useChatMessages();
  const threadHistory = useRecoilValue(threadHistoryState);
  const current = threadHistory?.currentThreadId || threadId;
  const { runs, loaded } = useRuns();

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    const tick = async () => {
      await refreshRuns();
      if (stopped) return;
      const busy =
        loadingRef.current ||
        Object.values(getRuns().runs).some((r) => isActive(r.state));
      timer = window.setTimeout(tick, busy ? FAST_MS : SLOW_MS);
    };
    tick();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshRuns();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // A question just started or ended here, or the user moved: read now
  // rather than at the next slow tick.
  useEffect(() => {
    refreshRuns();
  }, [loading, current]);

  const previous = useRef<Record<string, RunInfo> | null>(null);
  useEffect(() => {
    if (!loaded) return;
    const before = previous.current;
    previous.current = runs;
    if (!before) return; // the first read is old news, not an event
    for (const [id, run] of Object.entries(runs)) {
      if (id === current) continue;
      const was = before[id]?.state;
      if (was === run.state) continue;
      const open = {
        label: 'Open',
        onClick: () => navigate(`/thread/${id}`)
      };
      const title = run.title || 'Your other conversation';
      if (run.state === 'done' && isActive(was)) {
        toast(title, { description: 'Answer ready', action: open });
      } else if (run.state === 'needs_choice') {
        toast(title, { description: 'Needs your choice', action: open });
      }
    }
  }, [runs, loaded, current]);

  useEffect(() => {
    if (!current) return;
    const run = runs[current];
    if (
      run &&
      (run.state === 'done' || run.state === 'failed') &&
      document.visibilityState === 'visible'
    ) {
      markRunSeen(current);
    }
  }, [runs, current]);

  return null;
}
